import { ButtonCus } from '@/components/common/Button';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Textarea } from '@/components/common/Textarea';
import { ORDER_STATUS_CMS } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { ORDER_STATUS, OrderStatusType } from '@/types/enums';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface UpdateCancelOrderProps {
    id: number,
    reload?: Function
}
export default observer(function UpdateCancelOrder({ id, reload }: UpdateCancelOrderProps) {
    // hook
    const { t } = useTranslation();

    // store
    const { reservationStore: { updateReservationStatus, getReservationCMSDetail }, modalStore: { hideAllModals, showAlertModal } } = useStore();
    // validate schema
    const validateSchema = yup.object().shape({
        status: yup.number().required(),
        note: yup
            .string()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange'
    });

    // const [orderStatus, setOrderStatus] = useState<OrderStatusType | null>(null);
    const [listOrderStatus, setListOrderStatus] = useState([]);

    // const watchRefundStatus = form.watch('refundStatus');
    // const watchNote = form.watch('note');

    // lifecycle
    useEffect(() => {
        getDetail();
    }, []);

    // function
    const getDetail = async () => {
        const result = await flowResult(getReservationCMSDetail({ id }));
        if (result) {
            form.setValue('status', result.information.orderStatus);
            convertListAcceptOrderStatus(result.information.orderStatus);
        }
    };

    const convertListAcceptOrderStatus = (orderStatus: OrderStatusType) => {
        let acceptStatusList: any = [];
        switch (orderStatus) {
            case ORDER_STATUS.Not_Paid:
                acceptStatusList = [
                    ORDER_STATUS.Not_Paid, ORDER_STATUS.Paid, ORDER_STATUS.Available, ORDER_STATUS.Return, ORDER_STATUS.Cancel
                ];
                break;
            case ORDER_STATUS.Add_Not_Paid:
                acceptStatusList = [
                    ORDER_STATUS.Paid, ORDER_STATUS.Available, ORDER_STATUS.Return, ORDER_STATUS.Cancel, ORDER_STATUS.Add_Not_Paid
                ];
                break;
            case ORDER_STATUS.Paid:
                acceptStatusList = [
                    ORDER_STATUS.Paid, ORDER_STATUS.Available, ORDER_STATUS.Return, ORDER_STATUS.Cancel
                ];
                break;
            case ORDER_STATUS.Available:
                acceptStatusList = [
                    ORDER_STATUS.Available, ORDER_STATUS.Return
                ];
                break;
            case ORDER_STATUS.Operation:
                acceptStatusList = [
                    ORDER_STATUS.Operation, ORDER_STATUS.Return
                ];
                break;
            case ORDER_STATUS.Overdue:
                acceptStatusList = [
                    ORDER_STATUS.Return, ORDER_STATUS.Overdue
                ];
                break;
        }

        if (acceptStatusList.length > 0) {
            acceptStatusList = acceptStatusList.map(status => ({
                label: t(ORDER_STATUS_CMS[status].key),
                value: status.toString(),
                code: status.toString()
            }));
        }

        setListOrderStatus(acceptStatusList);
    };

    const onSubmit = async (data: FormData) => {
        let payload = {
            id,
            ...data
        };
        if (data.status === ORDER_STATUS.Cancel) {
            showAlertModal({
                id: 'confirm-cancel',
                size: 'sm',
                content: (
                    <p className='text-center whitespace-pre-line'>
                        {t('messages.confirm_cancel_booking')}
                    </p>
                ),
                isConfirmModal: true,
                saveButton: t('button.yes'),
                cancelButton: t('button.no'),
                onSave: async () => {
                    await onSubmitData(payload);
                }
            });
        } else {
            await onSubmitData(payload);
        }
    };

    const onSubmitData = async (payload) => {
        const res = await flowResult(updateReservationStatus(payload));
        if (res) {
            hideAllModals();
            showAlertModal({
                id: 'update-success',
                type: 'success',
                content: t('messages.update_success'),
                onClickXIcon: () => {
                    reload && reload();
                }
            });
        } else {
            showAlertModal({
                id: 'update-fail',
                type: 'error',
                content: t('messages.update_failure')
            });
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div>
                    <FormField
                        control={form.control}
                        name='status'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('field.reservation_status')}</FormLabel>
                                <FormControl>
                                    <Combobox
                                        value={field.value?.toString() ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.value);
                                        }}
                                        disableClearIcon
                                        search={false}
                                        items={
                                            listOrderStatus
                                        }
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                </div>
                <div className='mt-4'>
                    <FormField
                        control={form.control}
                        name='note'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('words_title.note')}</FormLabel>
                                <FormControl>
                                    <Textarea {...field} rows={5} maxLength={500} placeholder={t('placeholder.enter')}></Textarea>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div>
                    <div className='mt-8 flex justify-center'>
                        <ButtonCus variant='default' disabled={form.formState.isSubmitting} size='sm' type='submit'>
                            変更する
                            {/* {t('button.send')} */}
                        </ButtonCus>
                    </div>
                </div>
            </form>
        </Form>
    );
});
